import { z } from "zod";
import { US_STATES, type UsStateCode } from "./states";
import { MIN_ENROLLMENT_AGE, isAtLeastAgeYears } from "./age";
import type { MessageKey } from "../i18n/messages";
import type { MailingAddress } from "../types/api";

/**
 * The canonical empty `MailingAddress`: all strings, never `null`.
 *
 * It is the wire convention for "no separate mailing address". The customer
 * enrollment form and the admin patient-edit form both send it for the
 * unchecked case, because the backend `MailingAddress` field rejects an
 * explicit `null`.
 */
export function emptyMailingAddress(): MailingAddress {
  return { street1: "", street2: "", city: "", state: "", zip: "" };
}

const ZIP_RE = /^\d{5}(-\d{4})?$/;
const PO_BOX_RE = /\bp\.?\s?o\.?\s?box\b/i;
const DIGITS_ONLY = /\D/g;

// A complete street line carries a number and a name: at least one ASCII digit
// and at least two letters anywhere in the string. `11` and `Main St` fail;
// `1 Main`, `RR 2 Box 5` and `PSC 1234 Box 5678` pass. The backend applies the
// same two tests in `server/app/models/api/enrollments.py`.
const STREET_DIGIT_RE = /[0-9]/;
const STREET_LETTERS_RE = /\p{L}[\s\S]*\p{L}/u;

function isCompleteStreet(s: string): boolean {
  return STREET_DIGIT_RE.test(s) && STREET_LETTERS_RE.test(s);
}

/**
 * Medicare Beneficiary Identifier (MBI) in the CMS positional format: 11 characters, no hyphens.
 *
 * Position 1 is numeric 1-9 (never 0); positions 2, 5, 8, 9 are always letters
 * A-Z excluding the visually ambiguous S, L, O, I, B, Z; positions 4, 7, 10, 11
 * are always numeric; positions 3, 6 are a letter (same excluded set) or a
 * digit. `isValidMbi` upper-cases the input before testing, so the class is
 * uppercase-only.
 */
const MBI_RE =
  /^[1-9][ACDEFGHJKMNPQRTUVWXY][ACDEFGHJKMNPQRTUVWXY0-9][0-9][ACDEFGHJKMNPQRTUVWXY][ACDEFGHJKMNPQRTUVWXY0-9][0-9][ACDEFGHJKMNPQRTUVWXY][ACDEFGHJKMNPQRTUVWXY][0-9][0-9]$/;

/**
 * CMS synthetic MBIs of the Blue Button / BFD sandbox test beneficiaries (e.g. `1S00ABBAA00`).
 *
 * See the Synthetic Data Guide:
 * https://github.com/CMSgov/beneficiary-fhir-data/wiki/Synthetic-Data-Guide.
 * Position 2 is a literal `S`, CMS's synthetic-data marker. The real format
 * excludes `S` from every letter position, so this shape can't be a production
 * MBI. The other positions accept any alphanumeric, because CMS synthetic data
 * also breaks the positional letter/digit classes. The server mirrors this
 * regex as `_SYNTHETIC_MBI_RE` in `server/app/models/api/enrollments.py`.
 */
const SYNTHETIC_MBI_RE = /^[1-9]S[A-Z0-9]{9}$/;

/**
 * True when `value` is a valid MBI in the CMS positional format, or a CMS synthetic sandbox MBI.
 *
 * Accepting `SYNTHETIC_MBI_RE` lets Blue Button test beneficiaries pass
 * intake. It upper-cases before testing, so callers can pass raw user input.
 * It does not trim, so trim at the edge first.
 */
export function isValidMbi(value: string): boolean {
  const upper = value.toUpperCase();
  return MBI_RE.test(upper) || SYNTHETIC_MBI_RE.test(upper);
}

const STATE_CODE_TUPLE = US_STATES.map((s) => s.code) as [string, ...string[]];
const GENDER_VALUES = ["male", "female", "non_binary", "prefer_not_to_say"] as const;

/**
 * Spoken-language preference values; the customer form maps over these to render options.
 *
 * The stored and wire value is always this canonical lowercase English key;
 * only the displayed label is localized. Mirrors the backend
 * `PreferredLanguage` literal in `server/app/models/db/enrollment.py`.
 */
export const PREFERRED_LANGUAGE_VALUES = ["english", "spanish", "korean", "mandarin"] as const;
export type PreferredLanguage = (typeof PREFERRED_LANGUAGE_VALUES)[number];

type Translator = (key: MessageKey) => string;

// The 10-digit US phone rule, shared by the schema builders below and by
// standalone callers (e.g. the admin Add-to-Call free-form number entry).

/** Required phone: a non-empty string that reduces to exactly 10 digits. */
export function makePhoneSchema(t: Translator) {
  return z
    .string()
    .min(1, t("err.required"))
    .refine((s) => s.replace(DIGITS_ONLY, "").length === 10, t("err.phone"));
}

/**
 * Normalize a US phone number to canonical E.164 (`+1XXXXXXXXXX`).
 *
 * Client mirror of the backend `normalize_phone` (`server/app/lib/phone.py`):
 * strip to digits; an 11-digit number with a leading `1` drops that `1` (the
 * country code) before re-prefixing; the result must be exactly 10 digits.
 * Anything else throws, like the server's `ValueError`/422 contract.
 *
 * Call sites that already validated with {@link makePhoneSchema} (which pins
 * "reduces to 10 digits") can call this safely. The calls API
 * (`POST /admin/calls/credentials` and `…/participants`) requires this format.
 */
export function normalizePhone(raw: string): string {
  let digits = raw.replace(DIGITS_ONLY, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  if (digits.length !== 10) {
    throw new Error("invalid phone");
  }
  return `+1${digits}`;
}

/**
 * Non-throwing canonical form for cross-field comparison: the E.164 string from
 * {@link normalizePhone}, or `null` when `raw` is blank/invalid (so callers can
 * simply skip values that don't reduce to a valid 10-digit US number). Reuses
 * `normalizePhone` to keep the digit-strip + leading-`1` rule in one place and
 * leaves that helper's throwing contract intact.
 */
function canonicalPhoneOrNull(raw: string): string | null {
  try {
    return normalizePhone(raw);
  } catch {
    return null;
  }
}

/** Same as {@link makePhoneSchema} but treats an empty string as "not provided". */
export function makePhoneOptionalSchema(t: Translator) {
  return z
    .string()
    .refine((s) => s === "" || s.replace(DIGITS_ONLY, "").length === 10, t("err.phone"));
}

/** Optional email: empty string ("not provided") or a plausible address. */
export function makeEmailOptionalSchema(t: Translator) {
  return z.string().refine((v) => !v || /\S+@\S+\.\S+/.test(v), t("err.email"));
}

/** Required name part: non-blank after trimming. */
export function makeNameSchema(t: Translator) {
  return z.string().trim().min(1, t("err.required"));
}

/**
 * Required date of birth: a real calendar day in the past, as a bare
 * `YYYY-MM-DD` string, with no minimum age.
 *
 * The patient schema and the M3P review step use it as is; the enrollment
 * schema adds its MIN_ENROLLMENT_AGE floor on top.
 */
export function makeDobSchema(t: Translator) {
  return z
    .string()
    .min(1, t("err.required"))
    .refine((s) => {
      const ts = Date.parse(s);
      if (!Number.isFinite(ts) || ts >= Date.now()) return false;
      // An engine may roll an impossible day (`1980-02-30`) into the next
      // month instead of failing to parse it. A bare `YYYY-MM-DD` parses as
      // UTC, so a real day reads back unchanged.
      return new Date(ts).toISOString().slice(0, 10) === s;
    }, t("err.invalidDate"));
}

/** Required gender select: "" (nothing selected) fails with `err.selectOption`. */
export function makeGenderSchema(t: Translator) {
  return z
    .string()
    .min(1, t("err.selectOption"))
    .pipe(z.enum(GENDER_VALUES, { message: t("err.selectOption") }));
}

/**
 * Permanent (physical) address block: street1 required, complete (a number and
 * a name) and no PO box, city required, state a valid US code, ZIP of 5 or 5+4
 * digits.
 *
 * The enrollment and patient factories below and the M3P review step share it,
 * so the flow can never accept an address the create path would reject.
 */
export function makePermanentAddressSchema(t: Translator) {
  return z.object({
    street1: z
      .string()
      .min(1, t("err.required"))
      .refine((s) => !PO_BOX_RE.test(s), t("err.poBox"))
      .refine(isCompleteStreet, t("err.streetIncomplete")),
    street2: z.string(),
    city: z.string().min(1, t("err.required")),
    state: z
      .string()
      .min(1, t("err.selectState"))
      .pipe(z.enum(STATE_CODE_TUPLE, { message: t("err.selectState") })),
    zip: z.string().regex(ZIP_RE, t("err.zip")),
  });
}

/**
 * Mailing address block: five plain strings. Presence and format are enforced
 * only with a separate mailing address, by {@link refineMailingWhenSeparate}.
 */
export function makeMailingAddressSchema() {
  return z.object({
    street1: z.string(),
    street2: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  });
}

/**
 * The mailing-when-separate rule for a `.superRefine`: with
 * `hasSeparateMailingAddress` set, the mailing street, city, state and ZIP are
 * required, the street must be complete, the state a catalogue code and the
 * ZIP 5 or 5+4 digits. A PO box is allowed here, unlike the permanent street.
 * The enrollment, patient and M3P review schemas share it, and the backend
 * `_require_mailing_when_separate` mirrors it.
 */
export function refineMailingWhenSeparate(t: Translator) {
  return (
    data: { hasSeparateMailingAddress: boolean; mailing: MailingAddress },
    ctx: z.core.$RefinementCtx,
  ): void => {
    if (!data.hasSeparateMailingAddress) return;
    const m = data.mailing;
    const requiredKeys = ["street1", "city", "state", "zip"] as const;
    for (const k of requiredKeys) {
      if (!m[k] || m[k].trim() === "") {
        ctx.addIssue({ code: "custom", path: ["mailing", k], message: t("err.required") });
      }
    }
    if (m.street1.trim() !== "" && !isCompleteStreet(m.street1)) {
      ctx.addIssue({
        code: "custom",
        path: ["mailing", "street1"],
        message: t("err.streetIncomplete"),
      });
    }
    if (m.state && !STATE_CODE_TUPLE.includes(m.state)) {
      ctx.addIssue({ code: "custom", path: ["mailing", "state"], message: t("err.selectState") });
    }
    if (m.zip && !ZIP_RE.test(m.zip)) {
      ctx.addIssue({ code: "custom", path: ["mailing", "zip"], message: t("err.zip") });
    }
  };
}

/**
 * Build the enrollment zod schema bound to the active locale's translator.
 *
 * Issue messages follow the language toggle. The input and output shapes are
 * identical across locales (only the error copy differs), so the inferred
 * types below are stable.
 */
export function buildEnrollmentSchema(t: Translator) {
  const phone = makePhoneSchema(t);
  const phoneOptional = makePhoneOptionalSchema(t);

  const permanentAddress = makePermanentAddressSchema(t);
  const mailingAddress = makeMailingAddressSchema();
  const requireMailing = refineMailingWhenSeparate(t);

  // The customer form doesn't collect the pharmacy (the admin modal edits it),
  // so all fields are plain optional strings, like `mailingAddress`.
  const pharmacyAddress = z.object({
    street1: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  });

  // Preferred pharmacy, admin-only and absent from the customer-create payload
  // (see ENROLLMENT_CREATE_KEYS). Mirrors the backend `Pharmacy` sub-model. The
  // structured prescription list is not a form field; the admin manages it on
  // the Prescription tab.
  const pharmacy = z.object({
    name: z.string(),
    phone: phoneOptional,
    address: pharmacyAddress,
  });

  return z
    .object({
      firstName: makeNameSchema(t),
      lastName: makeNameSchema(t),
      suffix: z.string(),
      pronouns: z.enum(["", "she/her", "he/him", "they/them"]),

      dob: makeDobSchema(t)
        // Patient must be at least MIN_ENROLLMENT_AGE as of today. Both refines
        // run; this one comes after the past-date refine so a malformed or
        // future date surfaces `err.invalidDate` as its first issue.
        .refine((s) => isAtLeastAgeYears(s, MIN_ENROLLMENT_AGE), t("err.minAge")),

      gender: makeGenderSchema(t),

      permanent: permanentAddress,
      hasSeparateMailingAddress: z.boolean(),
      mailing: mailingAddress,

      // Optional Medicare-plan selection: the id of a `PlanOption` row from the
      // public `GET /plans/available?state=XX` list, `""` when none is chosen.
      // Never required; the UI only gates the combobox on a selected permanent
      // state, and the server validates a submitted id.
      planId: z.string(),

      phone,
      homePhone: phoneOptional,
      email: makeEmailOptionalSchema(t),
      // Spoken-language preference for calls. It always carries a value
      // (defaults to english), so it is a closed enum with no conditional rule.
      preferredLanguage: z.enum(PREFERRED_LANGUAGE_VALUES),

      // Optional at intake: blank (or whitespace, which trims to "") is
      // accepted, and the server persists "". A non-empty value must pass
      // `isValidMbi`.
      mbi: z
        .string()
        .trim()
        .refine((s) => s === "" || isValidMbi(s), t("err.mbi")),

      pharmacy,

      // Optional SMS-consent checkbox, part of the create payload (see
      // ENROLLMENT_CREATE_KEYS). It doesn't gate the form; the backend seeds the
      // SMS-consent table from it (`opted_in` when true, `opted_out` when false).
      smsConsent: z.boolean(),
    })
    .superRefine((data, ctx) => {
      requireMailing(data, ctx);

      // Cross-field contact uniqueness, mirroring the server's collision check
      // (the server is authoritative; this routes a duplicate onto the right
      // input before submit). The primary `phone` and secondary `homePhone`
      // compare as canonical E.164, so `(555) 555-1234` and `5555551234`
      // collide; values that don't reduce to a valid 10-digit US number are
      // skipped, since their own validators flag format. The collision
      // attaches to the duplicate field's path, never the primary `phone`.
      const primaryPhone = canonicalPhoneOrNull(data.phone);
      const homePhone = canonicalPhoneOrNull(data.homePhone);
      if (homePhone && homePhone === primaryPhone) {
        ctx.addIssue({ code: "custom", path: ["homePhone"], message: t("err.dupPhone") });
      }
    });
}

/**
 * Relaxed schema for the admin EditFormModal.
 *
 * It mirrors the backend `EnrollmentDetailsPatch` model, which carries only the
 * enrollment-owned editable field: the pharmacy. The
 * patient-derived fields (name, suffix, pronouns, dob, gender, addresses,
 * phone, homePhone, email, preferredLanguage, mbi) aren't part of the patch
 * body, because the server resolves them from the linked patient row and
 * ignores them if sent.
 *
 * The remaining fields keep the strict schema's format checks without any
 * required-presence or cross-field rule. An incomplete enrollment can be saved
 * and gap-filled field by field, while a provided but malformed value (bad
 * phone or email) is caught before the PUT.
 */
export function buildPartialEnrollmentSchema(t: Translator) {
  const phoneOptional = makePhoneOptionalSchema(t);

  // All-optional address sub-model, with no format checks on the patch path.
  const loosePharmacyAddress = z.object({
    street1: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  });

  return z.object({
    pharmacy: z.object({
      name: z.string(),
      phone: phoneOptional,
      address: loosePharmacyAddress,
    }),
  });
}

/**
 * Schema for the admin patient-edit form, mirroring the backend `PatientWrite` model.
 *
 * `PatientWrite` lives in `server/app/models/api/admin.py`. The schema covers
 * a strict subset of the enrollment schema's patient-derived fields (name,
 * suffix, pronouns, dob, gender, addresses, contact, language, mbi) with the
 * same field rules (`isValidMbi`, `makePhoneSchema`/`makePhoneOptionalSchema`,
 * the ZIP, PO-box and state rules, the mailing-when-separate check), so an
 * edit can never accept a value the public create path rejects.
 *
 * It differs from `buildEnrollmentSchema` in two ways, both mirroring
 * `PatientWrite`: `dob` requires only a valid past date, with no
 * MIN_ENROLLMENT_AGE floor, and the enrollment-owned pharmacy is absent.
 */
export function buildPatientSchema(t: Translator) {
  const phone = makePhoneSchema(t);
  const phoneOptional = makePhoneOptionalSchema(t);

  const permanentAddress = makePermanentAddressSchema(t);
  const mailingAddress = makeMailingAddressSchema();

  return z
    .object({
      firstName: makeNameSchema(t),
      lastName: makeNameSchema(t),
      suffix: z.string(),
      pronouns: z.enum(["", "she/her", "he/him", "they/them"]),

      // Valid past date only, with no minimum-age floor (see the factory doc).
      dob: makeDobSchema(t),

      gender: makeGenderSchema(t),

      permanent: permanentAddress,
      hasSeparateMailingAddress: z.boolean(),
      mailing: mailingAddress,

      email: makeEmailOptionalSchema(t),
      homePhone: phoneOptional,
      phone,
      preferredLanguage: z.enum(PREFERRED_LANGUAGE_VALUES),

      // Optional here too, with the enrollment schema's empty-or-valid rule, so
      // an admin edit can save a patient with no MBI on file.
      mbi: z
        .string()
        .trim()
        .refine((s) => s === "" || isValidMbi(s), t("err.mbi")),
    })
    .superRefine(refineMailingWhenSeparate(t));
}

// The schema's shape doesn't depend on locale, only its messages do, so the
// types are inferred once from a key-as-string build.

const _typeProbe = buildEnrollmentSchema((k) => k);

export type EnrollmentFormFields = z.input<typeof _typeProbe>;
export type EnrollmentFormValues = z.output<typeof _typeProbe>;

/**
 * Input (form-state) shape of the admin partial editor: the enrollment-owned editable fields only.
 *
 * See `buildPartialEnrollmentSchema`. It is a strict subset of the wide
 * `EnrollmentFormFields`, so admin form state built from this type can never
 * carry or submit a patient field.
 */
const _partialTypeProbe = buildPartialEnrollmentSchema((k) => k);
export type EnrollmentEditFields = z.input<typeof _partialTypeProbe>;

/**
 * Input (form-state) shape of the admin patient-edit form, mirroring the backend `PatientWrite`.
 *
 * All inputs are strings or booleans (selects start hydrated from the loaded
 * patient); the schema narrows `gender`, `pronouns` and `preferredLanguage` to
 * their closed enums on `safeParse`.
 */
const _patientTypeProbe = buildPatientSchema((k) => k);
export type PatientFormFields = z.input<typeof _patientTypeProbe>;

/** Empty defaults. Selects start at "" so the first submit surfaces required-field errors. */
export const enrollmentFormDefaults: EnrollmentFormFields = {
  firstName: "",
  lastName: "",
  suffix: "",
  pronouns: "",
  dob: "",
  gender: "",
  permanent: { street1: "", street2: "", city: "", state: "", zip: "" },
  hasSeparateMailingAddress: false,
  mailing: emptyMailingAddress(),
  planId: "",
  phone: "",
  homePhone: "",
  email: "",
  preferredLanguage: "english",
  mbi: "",
  pharmacy: { name: "", phone: "", address: { street1: "", city: "", state: "", zip: "" } },
  smsConsent: false,
};

/**
 * Top-level keys of the customer-create payload, the form-visible subset of the schema.
 *
 * They are the only form fields the public `POST /enrollments` model
 * (`EnrollmentCreate`, `extra="forbid"`) accepts; the non-form extras on the
 * `EnrollmentCreate` type below are merged at submit time. The schema, types
 * and defaults above stay wide; only the customer submission is narrowed via
 * `pickEnrollmentCreate`, and the CSV parser rejects columns outside this set.
 * The admin-only `pharmacy` key is absent.
 * `planId` is sent only when selected: `pickEnrollmentCreate` omits the
 * empty-string "no selection" value, because the backend field is an optional
 * `str | None` and "" is not a valid plan id.
 */
export const ENROLLMENT_CREATE_KEYS = [
  "firstName",
  "lastName",
  "suffix",
  "pronouns",
  "dob",
  "gender",
  "permanent",
  "hasSeparateMailingAddress",
  "mailing",
  "planId",
  "phone",
  "homePhone",
  "email",
  "preferredLanguage",
  "mbi",
  "smsConsent",
] as const;

/**
 * The wire shape for `POST /enrollments`, the form-visible subset of the wide `EnrollmentFormValues`.
 *
 * Mirrors the backend `EnrollmentCreate`, which forbids the admin-only fields.
 * The `as const` on `ENROLLMENT_CREATE_KEYS` makes this self-checking: a key
 * typo there fails typecheck here.
 *
 * `refId` is the optional referral token, mirroring the backend's optional
 * `ref_id`. It is not a form field: it is absent from the zod schema,
 * `enrollmentFormDefaults`, `ENROLLMENT_CREATE_KEYS` and the CSV parser. The
 * customer form merges it onto the body at submit time only when the page was
 * reached via a `?refId=` partner link.
 *
 * `pharmacyAgreementAccepted` mirrors the backend's optional
 * `pharmacy_agreement_accepted` and is not a form field either. It comes from
 * the pharmacy consent checkbox, as `refId` comes from the URL, and is merged
 * onto the body at submit time only for a Pharmacy referral, where the ticked
 * checkbox is what lets the submit through. It is not in
 * `ENROLLMENT_CREATE_KEYS`/`pickEnrollmentCreate`.
 *
 * `selfId` is the optional per-org self-enroll code from a `?selfId=` QR link,
 * mirroring the backend's optional `self_id`. Like `refId` it is not a form
 * field; it is merged onto the body at submit time only when the page was
 * reached via a self-enroll link. It is mutually exclusive with `refId` (the
 * backend 422s if both are sent); the customer form only ever sends one,
 * because `refId` takes precedence in the URL.
 *
 * `bbToken` is the optional Blue Button prefill handle, mirroring the backend's
 * optional `bb_token`. Like `refId`/`selfId` it is not a form field: the M3P
 * flow's sign step merges it onto the body from the flow state, which the
 * `?bb=<handle>` CMS callback on `/enroll/verifying` seeds. The `/form` route
 * never sends it. A submit-time `409 bb_prefill_invalid` or `bb_mbi_mismatch`
 * signals a dead handle.
 */
export type EnrollmentCreate = Omit<
  Pick<EnrollmentFormValues, (typeof ENROLLMENT_CREATE_KEYS)[number]>,
  "planId"
> & {
  /**
   * Optional plan selection, present only when the member picked a plan.
   * `pickEnrollmentCreate` omits the empty-string form default (the backend
   * accepts `plan_id: str | None`, and `""` is not a valid id).
   */
  planId?: string;
  /**
   * The contract year the plan is picked for, mirroring the backend's optional
   * `plan_year` (wire `planYear`) in server/app/models/api/enrollments.py;
   * change both sides together. An integer flag, not a form field: `/form`
   * and the M3P flow stamp it from the plan-year toggle whenever that toggle
   * renders, with or without a plan, and omit it otherwise (absent = the
   * current year). The server validates `planId` against that year's
   * available set.
   */
  planYear?: number;
  refId?: string;
  selfId?: string;
  bbToken?: string;
  pharmacyAgreementAccepted?: boolean;
  /**
   * Broker-referral consents, mirroring the backend's optional
   * `broker_agreement_accepted` / `broker_share_results`. Like the pharmacy flag
   * they are not form fields; they are merged onto the body at submit time only
   * when the referrer org (refId or selfId) is a Broker.
   */
  brokerAgreementAccepted?: boolean;
  brokerShareResults?: boolean;
  /**
   * M3P-flow markers, mirroring the backend `EnrollmentCreate`'s optional
   * `flow: "m3p" | None` and `m3p_urgency` (wire `m3pUrgency`) in
   * `server/app/models/api/enrollments.py`; change both sides together. Like
   * `refId`/`bbToken` they are not form fields, and the `/form` route never
   * sends them. The M3P flow's submit stamps `flow: "m3p"` plus the
   * requirements-step urgency choice carried in the flow state.
   */
  flow?: "m3p" | null;
  m3pUrgency?: "soon" | "planning" | null;
  /**
   * Optional medication-picker selection, mirroring the backend
   * `EnrollmentCreate.rxcuis` in `server/app/models/api/enrollments.py`; change
   * both sides together. It carries RxNorm CUIs only, never drug names: the
   * server resolves each against the drugs catalog into denormalized
   * prescription snapshots, drops an unknown rxcui, and caps the list at
   * `MAX_CREATE_RXCUIS`. Like the markers above it is not a form field, and the
   * `/form` route never sends it. The M3P flow's submit stamps it only when the
   * flow's urgency is "soon" and the picker has selections.
   */
  rxcuis?: string[] | null;
};

/**
 * Narrow a wide form value to the customer-create payload.
 *
 * It is a shallow pick of `ENROLLMENT_CREATE_KEYS`; the nested address objects
 * pass through by reference. The trimmed body keeps the backend's
 * `extra="forbid"` from rejecting a submission for carrying admin-only fields.
 */
export function pickEnrollmentCreate(values: EnrollmentFormValues): EnrollmentCreate {
  const out = {} as Record<string, unknown>;
  for (const key of ENROLLMENT_CREATE_KEYS) {
    // Send-only-when-selected: an unselected plan ("" form default) is omitted
    // entirely, matching the backend's optional `plan_id: str | None`.
    if (key === "planId" && values.planId === "") continue;
    out[key] = values[key];
  }
  // Safe by construction: we copied exactly the keys that define EnrollmentCreate.
  return out as EnrollmentCreate;
}

export { US_STATES };
export type { UsStateCode };
